import {isEmpty, trim} from 'lodash';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {agentActionsApi} from 'api';
import {Alert, Icon, TextArea, Toggle} from 'components';
import {useApiData, useUserSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {SessionNoteAccess} from 'types';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.NOTES_SIDEBAR;

export const AddNote: React.FC = () => {
    const {t} = useTranslation();
    const [noteMessage, setNoteMessage] = useState('');
    const [isPublic, setIsPublic] = useState(true);

    const newNoteAccess = useMemo(() => isPublic ? SessionNoteAccess.Public : SessionNoteAccess.Private, [isPublic]);

    const {sessionParams} = useUserSession();

    const resetNote = useCallback(() => {
        setNoteMessage('');
    }, []);

    const {send: addSessionNoteFn, loading: addNoteLoading, error: addNoteError} = useApiData(agentActionsApi.addNote, {
        onSuccess: resetNote
    });

    const addNoteDisabled = useMemo(() => isEmpty(trim(noteMessage)) || addNoteLoading, [noteMessage, addNoteLoading]);

    const addSessionNote = useCallback((message: string, access: SessionNoteAccess) => {
        addSessionNoteFn(sessionParams, {message, access});
    }, [sessionParams, addSessionNoteFn]);

    const handleAddNote = useCallback(() => {
        if (addNoteDisabled) {
            return;
        }

        addSessionNote(noteMessage, newNoteAccess);
    }, [noteMessage, newNoteAccess, addSessionNote, addNoteDisabled]);

    return (
        <div className='tw-border tw-border-default-dimmed tw-rounded-lg'>
            <TextArea
                disabled={addNoteLoading}
                value={noteMessage}
                rows={4}
                label={t(T_KEYS.MESSAGE_PLACEHOLDER)}
                onChange={setNoteMessage}
            />
            <div className='tw-flex tw-items-center tw-justify-between tw-px-2'>
                <Toggle
                    label={t(T_KEYS.PUBLIC_LABEL)}
                    labelPlacement='right'
                    name='note-public'
                    checked={isPublic}
                    size='small'
                    className='tw-my-4'
                    onChange={setIsPublic}
                />
                <div>
                    <Icon button name='send' color='primary' disabled={addNoteDisabled} onClick={handleAddNote} />
                </div>
            </div>
            {addNoteError && <Alert className='tw-my-4' type='danger'>{t(T_KEYS.ADD_NOTE_ERROR)}</Alert>}
        </div>
    );
};